import React, { useState } from 'react';
import { FadeIn } from './FadeIn';
import { ArrowRight, ChevronLeft, ChevronRight, MapPin, Quote } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const testimonials = [{
  name: 'Sarah M.',
  location: 'Austin, TX',
  original: '$4,200',
  final: '$2,980',
  saved: '$1,220',
  story: 'I requested an itemized statement for my hospital bill and found two duplicate charges and one upcoded procedure. Two calm phone calls later, the balance was adjusted.'
}, {
  name: 'Jennifer L.',
  location: 'Denver, CO',
  original: '$6,800',
  final: '$3,400',
  saved: '$3,400',
  story: 'My ER visit bill felt completely wrong. After demanding itemization, it revealed phantom charges for supplies I never received. The scripts gave me the confidence to push back.'
}, {
  name: 'Maria R.',
  location: 'Charlotte, NC',
  original: '$2,900',
  final: '$1,740',
  saved: '$1,160',
  story: 'I had an outpatient surgery and the facility fee was astronomical. I negotiated directly with the billing department using the exact scripts provided. It worked perfectly.'
}, {
  name: 'Amanda K.',
  location: 'Portland, OR',
  original: '$8,500',
  final: '$5,100',
  saved: '$3,400',
  story: 'My maternity bill was overwhelming. I used the insurance appeal framework and payment plan leverage to negotiate a massive reduction. I finally feel in control.'
}, {
  name: 'Rachel T.',
  location: 'Phoenix, AZ',
  original: '$3,600',
  final: '$2,160',
  saved: '$1,440',
  story: 'Specialist visits were draining my savings. I requested itemized statements and disputed several codes that seemed incorrect. The hospital agreed to lower the final amount.'
}];
const variants = {
  enter: (direction: number) => {
    return {
      x: direction > 0 ? 100 : -100,
      opacity: 0
    };
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => {
    return {
      zIndex: 0,
      x: direction < 0 ? 100 : -100,
      opacity: 0
    };
  }
};
export function RealExample() {
  const [[page, direction], setPage] = useState([0, 0]);
  const index = Math.abs(page % testimonials.length);
  const paginate = (newDirection: number) => {
    setPage([page + newDirection, newDirection]);
  };
  const setIndex = (newIndex: number) => {
    setPage([newIndex, newIndex > index ? 1 : -1]);
  };
  return <section className="section-container" data-id="element-194">
      <FadeIn data-id="element-195">
        <h2 className="text-3xl md:text-4xl mb-12 text-center text-brown-900" data-id="element-196">
          Real Results from Real Women
        </h2>

        <div className="relative max-w-4xl mx-auto" data-id="element-197">
          {/* Navigation Arrows - Desktop */}
          <button onClick={() => paginate(-1)} className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-6 lg:-translate-x-12 z-10 w-12 h-12 bg-white rounded-full shadow-md items-center justify-center text-brown-400 hover:text-terracotta-500 hover:shadow-lg transition-all border border-cream-200" aria-label="Previous testimonial" data-id="element-198">
            <ChevronLeft className="w-6 h-6" data-id="element-199" />
          </button>

          <button onClick={() => paginate(1)} className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-6 lg:translate-x-12 z-10 w-12 h-12 bg-white rounded-full shadow-md items-center justify-center text-brown-400 hover:text-terracotta-500 hover:shadow-lg transition-all border border-cream-200" aria-label="Next testimonial" data-id="element-200">
            <ChevronRight className="w-6 h-6" data-id="element-201" />
          </button>

          <div className="card-base relative overflow-hidden min-h-[380px] md:min-h-[350px]" data-id="element-202">
            <div className="absolute top-0 left-0 w-full h-1.5 bg-terracotta-500 z-20" data-id="element-203"></div>

            <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-204">
              <motion.div key={page} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              x: {
                type: 'spring',
                stiffness: 300,
                damping: 30
              },
              opacity: {
                duration: 0.2
              }
            }} className="w-full h-full flex flex-col justify-center p-4 md:p-8" data-id="element-205">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 items-center h-full" data-id="element-206">
                  {/* Story Side */}
                  <div className="space-y-3" data-id="element-207">
                    <Quote className="w-7 h-7 text-terracotta-200" data-id="element-208" />
                    <p className="text-sm md:text-lg text-brown-800 leading-relaxed italic" data-id="element-209">
                      "{testimonials[index].story}"
                    </p>
                    <div data-id="element-210">
                      <p className="font-bold text-brown-900 text-base" data-id="element-211">
                        {testimonials[index].name}
                      </p>
                      <div className="flex items-center text-brown-500 mt-0.5" data-id="element-212">
                        <MapPin className="w-3.5 h-3.5 mr-1" data-id="element-213" />
                        <span className="text-xs" data-id="element-214">
                          {testimonials[index].location}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Numbers Side */}
                  <div className="bg-cream-50 p-4 md:p-6 rounded-xl border border-cream-200 text-center flex flex-col justify-center" data-id="element-215">
                    <p className="text-brown-500 text-xs uppercase tracking-wide mb-2" data-id="element-216">
                      Final Result
                    </p>
                    <div className="flex flex-row items-center justify-center gap-2 sm:gap-3 mb-3" data-id="element-217">
                      <span className="text-xl md:text-2xl text-brown-400 line-through decoration-red-400 decoration-2" data-id="element-218">
                        {testimonials[index].original}
                      </span>
                      <ArrowRight className="text-terracotta-500 w-5 h-5" data-id="element-219" />
                      <span className="text-3xl md:text-4xl font-bold text-green-700" data-id="element-220">
                        {testimonials[index].final}
                      </span>
                    </div>
                    <div data-id="element-221">
                      <div className="inline-block bg-green-100 text-green-800 px-4 py-1.5 rounded-full font-bold text-sm md:text-base shadow-sm" data-id="element-222">
                        {testimonials[index].saved} protected
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Mobile Navigation & Dots */}
          <div className="flex items-center justify-center gap-6 mt-8" data-id="element-223">
            <button onClick={() => paginate(-1)} className="md:hidden w-10 h-10 bg-white rounded-full shadow-sm flex items-center justify-center text-brown-500 border border-cream-200" data-id="element-224">
              <ChevronLeft className="w-5 h-5" data-id="element-225" />
            </button>

            <div className="flex gap-2" data-id="element-226">
              {testimonials.map((_, i) => <button key={i} onClick={() => setIndex(i)} className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${index === i ? 'bg-terracotta-500 w-6' : 'bg-brown-200 hover:bg-brown-300'}`} aria-label={`Go to testimonial ${i + 1}`} data-id="element-227" />)}
            </div>

            <button onClick={() => paginate(1)} className="md:hidden w-10 h-10 bg-white rounded-full shadow-sm flex items-center justify-center text-brown-500 border border-cream-200" data-id="element-228">
              <ChevronRight className="w-5 h-5" data-id="element-229" />
            </button>
          </div>
        </div>

        <div className="text-center mt-12" data-id="element-230">
          <button onClick={() => {
          const el = document.getElementById('final-cta');
          if (el) el.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }} className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white bg-terracotta-500 rounded-full shadow-lg hover:bg-terracotta-600 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group" data-id="element-231">
            Get My Blueprint
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-232" />
          </button>
        </div>
      </FadeIn>
    </section>;
}